import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  GripVertical, Save, RotateCcw, Eye, EyeOff, Settings2,
  Activity, Users, ClipboardCheck, TrendingUp, AlertTriangle,
  UserPlus, BarChart3, Bell, Zap, Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const widgetIcons = {
  quality_score: Activity,
  census_adc: Users,
  compliance_rating: ClipboardCheck,
  active_pips: TrendingUp,
  burnout_risk: AlertTriangle,
  hiring_gap: UserPlus,
  clinical_chart: BarChart3,
  safety_chart: BarChart3,
  alerts_widget: Bell,
  quick_actions: Zap,
  staffing_overview: Users,
  benchmarks_widget: Target
};

function SortableWidget({ widget, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const Icon = widgetIcons[widget.id] || Settings2;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        widget.enabled 
          ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' 
          : 'bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800 opacity-60'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </button>
      
      <div className={`p-2 rounded-lg ${widget.enabled ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
        <Icon className={`h-4 w-4 ${widget.enabled ? 'text-teal-600' : 'text-gray-400'}`} />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm truncate ${!widget.enabled && 'text-gray-400'}`}>
          {widget.title}
        </p>
        <p className="text-xs text-muted-foreground">
          {widget.type === 'chart' ? 'Chart' : widget.type === 'alerts' ? 'Alerts' : 'Metric Card'}
        </p>
      </div>
      
      <Badge variant="outline" className="text-xs">
        {widget.size}
      </Badge>
      
      <Switch
        checked={widget.enabled}
        onCheckedChange={() => onToggle(widget.id)}
      />
    </div>
  );
}

export default function CustomizeDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const fetchLayout = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/layout`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWidgets(data.widgets);
      }
    } catch (error) {
      toast.error('Failed to load dashboard layout');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLayout();
  }, [token]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      setWidgets((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        setHasChanges(true);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const toggleWidget = (widgetId) => {
    setWidgets(widgets.map(w => 
      w.id === widgetId ? { ...w, enabled: !w.enabled } : w
    ));
    setHasChanges(true);
  };

  const saveLayout = async () => {
    setSaving(true);
    try {
      const updatedWidgets = widgets.map((w, idx) => ({ ...w, position: idx }));
      
      const response = await fetch(`${API_URL}/api/dashboard/layout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ widgets: updatedWidgets })
      });

      if (response.ok) {
        toast.success('Dashboard layout saved');
        setHasChanges(false);
      } else {
        toast.error('Failed to save layout');
      }
    } catch (error) {
      toast.error('Error saving layout');
    } finally {
      setSaving(false);
    }
  };

  const resetLayout = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/layout/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Dashboard reset to default');
        fetchLayout();
        setHasChanges(false);
      }
    } catch (error) {
      toast.error('Error resetting layout');
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = widgets.filter(w => w.enabled).length;
  const activeWidget = activeId ? widgets.find(w => w.id === activeId) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customize-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2">
            <Settings2 className="h-7 w-7 text-teal-500" />
            Customize Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Drag to reorder, toggle to show/hide widgets
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetLayout} disabled={saving}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button 
            size="sm" 
            onClick={saveLayout} 
            disabled={saving || !hasChanges}
            className="bg-gradient-to-r from-teal-500 to-cyan-500"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Layout'}
          </Button>
        </div>
      </div>

      {/* Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium">{enabledCount} Visible</span>
              </div>
              <div className="flex items-center gap-2">
                <EyeOff className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-muted-foreground">{widgets.length - enabledCount} Hidden</span>
              </div>
            </div>
            {hasChanges && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                Unsaved Changes
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Widget List */}
      <Card>
        <CardHeader>
          <CardTitle>Dashboard Widgets</CardTitle>
        </CardHeader>
        <CardContent>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={widgets.map(w => w.id)} strategy={rectSortingStrategy}>
              <div className="space-y-2">
                {widgets.map((widget) => (
                  <SortableWidget
                    key={widget.id}
                    widget={widget}
                    onToggle={toggleWidget}
                  />
                ))}
              </div>
            </SortableContext>
            
            <DragOverlay>
              {activeWidget && (
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-gray-800 shadow-lg">
                  <GripVertical className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{activeWidget.title}</span>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card className="bg-gray-50 dark:bg-gray-900/50">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            <strong>Tip:</strong> Drag widgets to reorder them on your dashboard. 
            Toggle the switch to show or hide widgets. Click "Save Layout" to apply your changes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
